<script setup lang="ts">
    import { onMounted, reactive, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { getTask, cancelTask, runTask, deleteTask, Task, saveTaskNotification } from './TaskListing.access';
    import Loading from '@shared/components/Loading.vue';
    import { getLabelType, formatCron } from './TaskListing.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';

    const props = defineProps<{
        taskId: string;
    }>();

    const router = useRouter();

    const state = reactive<{
        task: Task | null;
        isLoading: boolean;
    }>({
        task: null,
        isLoading: true,
    });

    function goBack() {
        if (window.history.length > 1) {
            router.back();
        } else {
            router.replace('/Automation/Tasks');
        }
    }

    onMounted(async () => {
        state.task = await getTask(props.taskId).finally(() => (state.isLoading = false));
    });

    async function onCancelTask(): Promise<void> {
        if (state.isLoading) return;
        state.isLoading = true;
        if (!state.task) return;

        await cancelTask(state.task.TaskId)
            .then(() => {
                bsAlert({ type: AlertTypes.Success });
                state.task!.StatusName = 'Aborted';
            })
            .catch(() => bsAlert({ type: AlertTypes.Danger, content: 'Failed to cancel task' }))
            .finally(() => {
                state.isLoading = false;
            });
    }

    async function onRunTask(): Promise<void> {
        if (state.isLoading) return;
        state.isLoading = true;
        if (!state.task) return;

        await runTask(state.task.TaskId)
            .then(() => {
                bsAlert({ type: AlertTypes.Success });
                state.task!.StatusName = 'Scheduled';
            })
            .catch(() => bsAlert({ type: AlertTypes.Danger, content: 'Failed to reschedule task' }))
            .finally(() => {
                state.isLoading = false;
            });
    }

    async function onDeleteTask(): Promise<void> {
        if (state.isLoading) return;
        state.isLoading = true;
        if (!state.task) return;

        await deleteTask(state.task.TaskId)
            .then(() => {
                bsAlert({ type: AlertTypes.Success });
                router.push('/Automation/Tasks');
            })
            .catch(() => bsAlert({ type: AlertTypes.Danger, content: 'Failed to delete task' }))
            .finally(() => {
                state.isLoading = false;
            });
    }

    const columnMappingEntries = computed(() =>
        Object.entries(state.task?.ImportData?.ColumnMapping ?? {}).filter(([, value]) => value != null && value !== '')
    );

    async function onToggleNotification(task: Task): Promise<void> {
        if (state.isLoading) return;
        state.isLoading = true;

        task.DontSendSuccessNotification = !task.DontSendSuccessNotification;

        await saveTaskNotification(task.TaskId, task.DontSendSuccessNotification)
            .then(() => {
                bsAlert({ type: AlertTypes.Success });
            })
            .catch(() => {
                task.DontSendSuccessNotification = !task.DontSendSuccessNotification;
                bsAlert({ type: AlertTypes.Danger });
            })
            .finally(() => {
                state.isLoading = false;
            });
    }

    const getSourceDisplay = (task: Task): string | null => {
        if (task.TypeName === 'Import' && task.ImportData) {
            return task.ImportData.SourceUrl || task.ImportData.SourceFile || null;
        }
        return null;
    };

    const showCancelAndRunBtns = computed(() => {
        if (!state.task || !state.task.ImportData) return true;

        return !state.task.ImportData.SourceFile;
    });
</script>

<template>
    <Loading :show="state.isLoading" loadingStyle="progress-bar" :fixed="true" />

    <div v-if="state.task" class="container-fluid p-lg-t">
        <p class="fw-600">
            <a href="#" @click.prevent="goBack">
                <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
                {{ $Resources.BackButton }}
            </a>
        </p>

        <h1>
            {{ state.task.ObjectName || $Resources.NoNamePlaceholder }}
            <small>{{ $Resources['TaskType' + state.task.TypeName] }}</small>
            <span :class="'mini-label mini-label-' + getLabelType(state.task.StatusName) + ' m-sm-l'">
                {{ $Resources['TaskStatus' + state.task.StatusName] }}
            </span>
        </h1>

        <p class="meta m-lg-b">
            <span class="glyphicon glyphicon-time"></span>
            {{ $Resources.CreatedLabel }}
            <time :datetime="state.task.Created">{{ formatDateTime(state.task.Created) }}</time>

            <span class="glyphicon glyphicon-user"></span>
            {{ $Resources.ModifiedByLabel }}
            <span>{{ state.task.CreatedBy?.Name }}</span>
        </p>

        <div class="form-group">
            <FormCheckable
                :disabled="state.isLoading"
                :checked="!state.task.DontSendSuccessNotification"
                :label="$Resources.SendSuccessNotificationLabel"
                :switchStyle="SwitchStyle.RedToGreen"
                @update:modelValue="onToggleNotification(state.task)" />
        </div>

        <div class="row">
            <div class="col-sm-6">
                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.TaskScheduledLbl }}:</span>
                    {{ formatDateTime(state.task.Scheduled) || '-' }}
                </div>

                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.TaskStartedLbl }}:</span>
                    {{ formatDateTime(state.task.Started) || '-' }}
                </div>

                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.TaskEndedLbl }}:</span>
                    {{ formatDateTime(state.task.Ended) || '-' }}
                </div>

                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.TaskIsRecurringLbl }}:</span>
                    {{ formatCron(state.task.CronPattern) || '-' }}
                </div>
            </div>
            <div class="col-sm-6">
                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.DataSourceNameLabel }}:</span>
                    {{ state.task.DataSource?.Name || '-' }}
                </div>

                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.DataSourceDescriptionLabel }}:</span>
                    {{ state.task.DataSource?.Description || '-' }}
                </div>

                <div class="m-lg-b">
                    <span class="label-look d-block">{{ $Resources.ConnectionNameLabel }}:</span>
                    {{ state.task.DataSource?.ConnectionName || '-' }}
                </div>
            </div>
        </div>

        <template v-if="state.task.ImportData">
            <h2 class="h3">{{ $Resources.ImportDataHeading }}</h2>

            <div class="row m-lg-t">
                <div class="col-sm-6">
                    <div v-if="getSourceDisplay(state.task)" class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.GenericSource }}:</span>
                        {{ getSourceDisplay(state.task) }}
                    </div>
                    <div v-if="state.task.ImportData.TargetListId" class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.GoToListLabel }}:</span>

                        <a :href="`/Lists/${state.task.ImportData.TargetListId}`">
                            {{ state.task.ObjectName || $Resources.NoNamePlaceholder }}
                        </a>
                    </div>
                    <div v-if="state.task.ImportData.TargetListDescription" class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.ListDescriptionLabel }}:</span>
                        {{ state.task.ImportData.TargetListDescription }}
                    </div>
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.ListImportReplaceContactsLabel }}:</span>
                        {{ state.task.ImportData.ReplaceExistingList ? $Resources.YesLabel : $Resources.NoLabel }}
                    </div>
                </div>
                <div class="col-sm-6">
                    <div v-if="state.task.ImportData.DefaultCountryCode" class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.DefaultCountryCodeLabel }}:</span>
                        {{ state.task.ImportData.DefaultCountryCode }}
                    </div>
                    <div v-if="state.task.ImportData.DefaultSourceName" class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.DefaultSourceNameLabel }}:</span>
                        {{ state.task.ImportData.DefaultSourceName }}
                    </div>
                    <div v-if="state.task.ImportData.DefaultOptInDate" class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.DefaultOptInDateLabel }}:</span>
                        {{ formatDateTime(state.task.ImportData.DefaultOptInDate) }}
                    </div>
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.IntegrationClearAllExistingMappedField }}:</span>
                        {{ state.task.ImportData.KeepOldValuesWhenEmpty ? $Resources.YesLabel : $Resources.NoLabel }}
                    </div>
                    <div class="m-lg-b">
                        <span class="label-look d-block">{{ $Resources.HasDataPointsLabel }}:</span>
                        {{ state.task.ImportData.HasDataPoints ? $Resources.YesLabel : $Resources.NoLabel }}
                    </div>
                </div>
            </div>
        </template>

        <div v-if="columnMappingEntries.length">
            <h2 class="h3">{{ $Resources.ColumnMappingHeading }}</h2>

            <table class="table table-hover m-lg-t">
                <thead>
                    <tr>
                        <th>{{ $Resources.ListImportMatchColumnsFieldsColumn }}</th>
                        <th>{{ $Resources.ListImportMatchColumnsSourceFieldsColumn }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="[field, column] in columnMappingEntries" :key="field">
                        <td>{{ field }}</td>
                        <td>{{ column }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="form-group d-flex gap-sm flex-wrap m-sm-t">
            <template v-if="showCancelAndRunBtns">
                <button
                    v-if="state.task.StatusName === 'Scheduled' || state.task.StatusName === 'Running'"
                    class="btn btn-default"
                    :disabled="state.isLoading"
                    @click="onCancelTask">
                    {{ $Resources.TaskCancelBtn }}
                </button>
                <button
                    v-if="state.task.StatusName === 'Failed' || state.task.StatusName === 'Scheduled' || state.task.StatusName === 'RescheduleFailed'"
                    class="btn btn-primary"
                    :disabled="state.isLoading"
                    @click="onRunTask">
                    {{ $Resources.TaskRunNowBtn }}
                </button>
            </template>
            <button
                v-if="state.task.StatusName === 'Failed' || state.task.StatusName === 'Aborted' || state.task.StatusName === 'RescheduleFailed'"
                class="btn btn-danger"
                :disabled="state.isLoading"
                @click="onDeleteTask">
                {{ $Resources.TaskDeleteBtn }}
            </button>
        </div>
    </div>
</template>
